import { useMemo } from "react"
import { Link, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAuth } from "@/features/auth/AuthContext"
import { useEffectiveAdminAccess } from "@/features/admin/useEffectiveAdminAccess"
import {
  PORTAL_ADMIN,
  PORTAL_DEPOSIT,
  PORTAL_EVENTS,
  PUBLIC_DEPOSITS,
} from "@/app/router/portalRoutes"
import { isDepositsUiEnabled } from "@/features/deposits/depositsUiEnabled"
import { publicAssetUrl } from "@/lib/publicAssetUrl"
import { footerSocialLinkClassName, footerSocialLinks } from "@/widgets/layout/footerSocialLinks"

type FooterLink = { labelKey: string; path: string; openInNewTab?: boolean }

const infoLinks: FooterLink[] = [
  { labelKey: "footer.aboutLibrary", path: "/about" },
  { labelKey: "footer.contacts", path: "/contacts" },
  { labelKey: "footer.terms", path: "/help/agreement", openInNewTab: true },
  { labelKey: "footer.personalDataConsent", path: "/help/personal", openInNewTab: true },
  { labelKey: "footer.personalDataProcessingPolicy", path: "/help/privacy", openInNewTab: true },
]

export default function Footer() {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const { isAuthenticated } = useAuth()
  const { hasAnyAdminAccess, adminAccessResolved } = useEffectiveAdminAccess()

  const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (pathname !== "/") return
    e.preventDefault()
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const portalLinks = useMemo<FooterLink[]>(() => {
    const links: FooterLink[] = [
      { labelKey: "nav.home", path: "/" },
      { labelKey: "nav.events", path: isAuthenticated ? PORTAL_EVENTS : "/events" },
    ]
    if (isDepositsUiEnabled)
      links.push({ labelKey: "nav.deposit", path: isAuthenticated ? PORTAL_DEPOSIT : PUBLIC_DEPOSITS })
    if (isAuthenticated && adminAccessResolved && hasAnyAdminAccess) {
      links.push({ labelKey: "nav.admin", path: PORTAL_ADMIN })
    }
    return links
  }, [adminAccessResolved, isAuthenticated, hasAnyAdminAccess])

  const renderLink = (link: FooterLink) => (
    <Link
      key={`${link.labelKey}-${link.path}`}
      to={link.path}
      onClick={link.path === "/" ? handleHomeClick : undefined}
      className="text-lib-text-secondary hover:text-lib-text text-sm font-medium transition-colors py-1 block"
      {...(link.openInNewTab ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
      {t(link.labelKey)}
    </Link>
  )

  return (
    <footer className="bg-lib-card border-t border-lib-border">
      <div className="w-full px-6 md:px-10 lg:px-16 pt-16 md:pt-20 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          <div className="lg:col-span-5 flex flex-col gap-6">
            <Link to="/" onClick={handleHomeClick} aria-label="Российская государственная библиотека">
              <img
                src={publicAssetUrl("images/rgb-logo.svg")}
                alt="Российская государственная библиотека"
                className="h-12 w-auto"
              />
            </Link>
            <p className="text-lib-text-secondary text-sm max-w-sm leading-relaxed">{t("footer.tagline")}</p>
          </div>

          <div className="lg:col-span-3 flex flex-col gap-1">
            <p className="text-lib-text-muted text-xs uppercase tracking-wider mb-2">{t("footer.portalTitle")}</p>
            {portalLinks.map(renderLink)}
          </div>

          <div className="lg:col-span-4 flex flex-col gap-6">
            <div className="flex flex-col gap-1">
              <p className="text-lib-text-muted text-xs uppercase tracking-wider mb-2">{t("footer.infoTitle")}</p>
              {infoLinks.map(renderLink)}
            </div>
            <div>
              <p className="text-lib-text-muted text-xs uppercase tracking-wider mb-3">
                {t("footer.socialTitle", "Мы в социальных сетях")}
              </p>
              <div className="flex flex-wrap items-center gap-3">
                {footerSocialLinks.map((link) => (
                  <a
                    key={link.href}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={footerSocialLinkClassName}
                    aria-label={t(link.labelKey)}
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-6 md:px-10 lg:px-16 border-t border-lib-border py-6 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <p className="text-lib-text-muted text-xs">{t("footer.copyright")}</p>
        <p className="text-lib-text-muted text-xs md:text-right md:max-w-xl">
          {t("footer.photoCreditsLead")}{" "}
          <a
            href="https://commons.wikimedia.org/wiki/Category:Russian_State_Library_building"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-lib-text transition-colors"
          >
            Wikimedia Commons
          </a>{" "}
          ({t("footer.photoCreditsLicense")}).
        </p>
      </div>
    </footer>
  )
}
